import {AfterViewInit, ApplicationRef, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {Whiteboard} from '@app/state/whiteboard.state';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {KeyCode} from '@app/modules/shared/data/constants';
import {Store} from '@ngxs/store';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {SVGEvent} from '@app/modules/svg/dataSource/constant/SVGEvent';
import {TableView} from '@app/modules/svg/element/complex/TableView';
import {NumberLineView2} from '@app/modules/svg/element/complex/cartesian2/NumberLineView2';
import {CoordinatePlaneView2} from '@app/modules/svg/element/complex/cartesian2/CoordinatePlaneView2';
import {PointedView} from '@app/modules/svg/element/shape/pointed/PointedView';
import {CartesianView} from '@app/modules/svg/element/complex/cartesian/CartesianView';
import {GraphicView} from '@app/modules/svg/element/complex/cartesian/GraphicView';
import {RayView} from '@app/modules/svg/element/complex/cartesian/RayView';
import {ElementView} from '@app/modules/svg/element/ElementView';
import {Point} from '@app/modules/svg/model/Point';
import {Tool} from '@app/modules/svg/service/tool/Tool';
import {DrawTool} from '@app/modules/svg/service/tool/draw/DrawTool';
import {Drawable} from '@app/modules/svg/service/tool/draw/type/Drawable';
import {ClickDrawer} from '@app/modules/svg/service/tool/draw/mode/ClickDrawer';
import {DrawRay} from '@app/modules/svg/service/tool/draw/element/complex/cartesian/DrawRay';
import {GeoGebraService} from '@app/modules/whiteboard/services/geo-gebra.service';
import {ForeignObjectView, ResizeMode} from '@app/modules/svg/element/foreign/ForeignObjectView';
import {TextBoxView} from '@app/modules/svg/element/foreign/text/TextBoxView';
import {Size} from '@app/modules/svg/model/Size';

@Component({
  selector: 'app-whiteboard-menus',
  templateUrl: './whiteboard-menus.component.html',
  styleUrls: ['./whiteboard-menus.component.css']
})
export class WhiteboardMenusComponent implements OnInit, AfterViewInit {

  @Input()
  public whiteboard: Whiteboard;
  public localUser: LocalUser;

  protected readonly ResizeMode = ResizeMode;

  public clickDrawing: boolean = false;
  public canEdit: boolean = false;
  public showEditButton: boolean = false;
  public showResizeModeButton: boolean = false;
  public resizeMode: ResizeMode;
  public canEditVertexes: boolean = false;
  public canEditTable: boolean = false;
  public canEditCoordinatePlane: boolean = false;
  public canEditNumberLine: boolean = false;
  public numberSizes: number[] = [10, 15, 20];
  public editCoordinatePlaneMenuFields = {
    xAxis: {
      label: 'X',
      min: -10,
      step: 1,
      numberOfSteps: 20,
      linesPerStep: 0
    },
    yAxis: {
      label: 'Y',
      min: -10,
      step: 1,
      numberOfSteps: 20,
      linesPerStep: 0
    },
    grid: {
      show: true,
      byX: 1,
      byY: 1
    },
    numberSize: this.numberSizes[0],
    showNumbers: true,
    showBorder: true
  };
  public editNumberLineMenuFields = {
    xAxis: {
      label: 'X',
      min: -10,
      step: 1,
      numberOfSteps: 20,
      linesPerStep: 0
    },
    numberSize: this.numberSizes[0],
    showNumbers: true,
    showBorder: false
  };
  public activeTable = { cols: 4, rows: 3 };

  private whiteboardWrapper: HTMLDivElement;
  @ViewChild('editButton', {static: true})
  private editButton: ElementRef;
  @ViewChild('resizeModeButton', {static: true})
  private resizeModeButton: ElementRef;
  @ViewChild('editCoordinatePlaneMenu', {static: true})
  private editCoordinatePlaneMenu: ElementRef;
  @ViewChild('numberLineEditMenu', {static: true})
  private numberLineEditMenu: ElementRef;
  @ViewChild('editTableMenu', {static: true})
  private tableEditMenu: ElementRef;
  @ViewChild('crossButton' , {static:true})
  protected crossButton: ElementRef;

  @Input()
  public whiteboardWrapperId: string;

  @Input()
  private whiteboardService: WhiteboardService;

  public KeyCode = KeyCode;

  constructor(
    private geogebraService: GeoGebraService,
    private applicationRef: ApplicationRef,
    private store: Store
  ) {}

  public ngOnInit(): void {
    this.listenStoreChanges();

    this.whiteboardWrapper = document.getElementById(this.whiteboardWrapperId) as HTMLDivElement;

    this.whiteboardWrapper.onscroll = () => {
      this.repositionCross();
      this.repositionAllElements();
    };

    this.whiteboardService.onInit.subscribe((whiteboard: Whiteboard): void => {
      this.whiteboard = whiteboard;
      this.init();
    });
  }

  public ngAfterViewInit(): void {
    this.whiteboardService.getState().subscribe();
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState.getLocalUser).subscribe((user: LocalUser) => {
      this.localUser = user;
    });
  }

  private init(): void {
    this.whiteboard.container.addCallBack(SVGEvent.DRAG_MOUSE_MOVE, this.repositionAllElements.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.ELEMENTS_DRAGGED, this.repositionAllElements.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.ROTATE_MOUSE_MOVE, this.repositionAllElements.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.RESIZE_MOUSE_MOVE, this.repositionAllElements.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.DRAW_MOUSE_DOWN, this.drawClickCallBack.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.ELEMENTS_FOCUSED, this.focusChangedCallBack.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.ALL_BLURRED, this.focusChangedCallBack.bind(this));
    this.whiteboard.container.addCallBack(SVGEvent.STOP_CLICK_DRAWING, this.stopClickDrawingCallBack.bind(this));
  }

  public changeRows(): void {
    const element = this.whiteboard.container.tools.editTableTool.editableElement;
    if (element instanceof TableView) {
      if (this.activeTable.rows - element.rows.length < 0) {
        element.removeRow();
      } else {
        const previousRowHeight: number = element.rows[element.rows.length - 1].height;
        element.addRow(previousRowHeight);
      }
    }
  }
  public changeCols(): void {
    const element = this.whiteboard.container.tools.editTableTool.editableElement;
    if (element instanceof TableView) {
      if (this.activeTable.cols - element.cols.length < 0) {
        element.removeCol();
      } else {
        const previousColWidth: number = element.cols[element.cols.length - 1].width;
        element.addCol(previousColWidth);
      }
    }
  }
  public setNumberLineProperties(): void {
    this.canEditNumberLine = false;
    this.showEditButton = true;
    const [firstFocusedElement] = this.whiteboard.container.focused.children;
    if (firstFocusedElement instanceof NumberLineView2) {
      this.editNumberLineMenuFields.xAxis.numberOfSteps = Math.min(
        this.editNumberLineMenuFields.xAxis.numberOfSteps,
        100
      );

      firstFocusedElement.edit(
        this.whiteboardService.copyObject(this.editNumberLineMenuFields.xAxis),
        this.editNumberLineMenuFields.numberSize,
        this.editNumberLineMenuFields.showNumbers,
        this.editNumberLineMenuFields.showBorder
      );
    }
  }
  public setCoordinatePlaneProperties(): void {
    this.canEditCoordinatePlane = false;
    this.showEditButton = true;
    const [firstFocusedElement] = this.whiteboard.container.focused.children;
    if (firstFocusedElement instanceof CoordinatePlaneView2) {
      this.editCoordinatePlaneMenuFields.xAxis.numberOfSteps = Math.min(
        this.editCoordinatePlaneMenuFields.xAxis.numberOfSteps,
        100
      );
      this.editCoordinatePlaneMenuFields.yAxis.numberOfSteps = Math.min(
        this.editCoordinatePlaneMenuFields.yAxis.numberOfSteps,
        100
      );
      firstFocusedElement.edit(
        this.whiteboardService.copyObject(this.editCoordinatePlaneMenuFields.xAxis),
        this.whiteboardService.copyObject(this.editCoordinatePlaneMenuFields.yAxis),
        this.whiteboardService.copyObject(this.editCoordinatePlaneMenuFields.grid),
        this.editCoordinatePlaneMenuFields.numberSize,
        this.editCoordinatePlaneMenuFields.showNumbers,
        this.editCoordinatePlaneMenuFields.showBorder
      );
    }
  }
  public edit(): void {
    const [firstFocusedElement] = this.whiteboard.container.focused.children;

    if (firstFocusedElement instanceof PointedView) {
      this.whiteboard.container.tools.editNodeTool.on();
    } else if (firstFocusedElement instanceof TableView) {
      this.canEditTable = true;
      this.whiteboard.container.tools.editTableTool.on();

      this.activeTable = {
        rows: firstFocusedElement.rows.length,
        cols: firstFocusedElement.cols.length
      };

      this.repositionMenu(this.whiteboard.container.tools.editTableTool.editableElement, this.tableEditMenu.nativeElement, {x: 0, y: -80});
    } else if (firstFocusedElement instanceof CoordinatePlaneView2) {
      this.canEditCoordinatePlane = true;
      this.applicationRef.tick(); /* force update ui to make coordinate plane menu visible */

      const xAxis = this.whiteboardService.copyObject(firstFocusedElement.xAxisModel);
      const yAxis = this.whiteboardService.copyObject(firstFocusedElement.yAxisModel);
      const grid = this.whiteboardService.copyObject(firstFocusedElement.gridModel);

      if (!xAxis.min) { xAxis.min = 0; }
      if (!xAxis.step) { xAxis.step = 0; }
      if (!xAxis.numberOfSteps) { xAxis.numberOfSteps = 0; }
      if (!xAxis.linesPerStep) { xAxis.linesPerStep = 0; }

      if (!yAxis.min) { yAxis.min = 0; }
      if (!yAxis.step) { yAxis.step = 0; }
      if (!yAxis.numberOfSteps) { yAxis.numberOfSteps = 0; }
      if (!yAxis.linesPerStep) { yAxis.linesPerStep = 0; }

      if (!grid.byX) { grid.byX = 0; }
      if (!grid.byY) { grid.byY = 0; }

      this.editCoordinatePlaneMenuFields.xAxis.label = xAxis.label;
      this.editCoordinatePlaneMenuFields.xAxis.min = xAxis.min;
      this.editCoordinatePlaneMenuFields.xAxis.step = xAxis.step;
      this.editCoordinatePlaneMenuFields.xAxis.numberOfSteps = xAxis.numberOfSteps;
      this.editCoordinatePlaneMenuFields.xAxis.linesPerStep = xAxis.linesPerStep;

      this.editCoordinatePlaneMenuFields.yAxis.label = yAxis.label;
      this.editCoordinatePlaneMenuFields.yAxis.min = yAxis.min;
      this.editCoordinatePlaneMenuFields.yAxis.step = yAxis.step;
      this.editCoordinatePlaneMenuFields.yAxis.numberOfSteps = yAxis.numberOfSteps;
      this.editCoordinatePlaneMenuFields.yAxis.linesPerStep = yAxis.linesPerStep;

      this.editCoordinatePlaneMenuFields.grid.show = grid.show;
      this.editCoordinatePlaneMenuFields.numberSize = firstFocusedElement.numberSize;
      this.editCoordinatePlaneMenuFields.showNumbers = firstFocusedElement.numbersVisible;
      this.editCoordinatePlaneMenuFields.showBorder = firstFocusedElement.borderVisible;
      this.editCoordinatePlaneMenuFields.grid.byX = grid.byX;
      this.editCoordinatePlaneMenuFields.grid.byY = grid.byY;

      this.repositionMenu(firstFocusedElement, this.editCoordinatePlaneMenu.nativeElement);
    } else if (firstFocusedElement instanceof NumberLineView2) {
      this.canEditNumberLine = true;
      this.applicationRef.tick(); /* force update ui to make number line menu visible */

      const numberLineEditMenu = document.getElementById('number-line-edit-menu');

      const xAxis = this.whiteboardService.copyObject(firstFocusedElement.xAxisModel);

      if (!xAxis.min) { xAxis.min = 0; }
      if (!xAxis.step) { xAxis.step = 0; }
      if (!xAxis.numberOfSteps) { xAxis.numberOfSteps = 0; }
      if (!xAxis.linesPerStep) { xAxis.linesPerStep = 0; }

      this.editNumberLineMenuFields.xAxis.label = xAxis.label;
      this.editNumberLineMenuFields.xAxis.min = xAxis.min;
      this.editNumberLineMenuFields.xAxis.step = xAxis.step;
      this.editNumberLineMenuFields.xAxis.numberOfSteps = xAxis.numberOfSteps;
      this.editNumberLineMenuFields.xAxis.linesPerStep = xAxis.linesPerStep;

      this.editNumberLineMenuFields.numberSize = firstFocusedElement.numberSize;
      this.editNumberLineMenuFields.showNumbers = firstFocusedElement.numbersVisible;

      this.repositionMenu(firstFocusedElement, numberLineEditMenu);
      this.showEditButton = false;
    } else if (
      firstFocusedElement instanceof CartesianView ||
      firstFocusedElement instanceof GraphicView ||
      firstFocusedElement instanceof RayView
    ) {
      this.whiteboard.container.tools.editCartesianTool.on();
    } else if (firstFocusedElement instanceof ForeignObjectView && firstFocusedElement.SVG.getAttribute('data-class') === 'ggb-applet') {
      firstFocusedElement.pointerEvents = true;
      this.whiteboard.container.tools.activeTool.off(false);
      this.whiteboard.container.blur(undefined, false);
    }

    this.showEditButton = false;
  }
  public setResizeMode(resizeMode: ResizeMode): void {
    const [firstFocusedElement] = this.whiteboard.container.focused.children;

    if (firstFocusedElement instanceof ForeignObjectView) {
      const oldSize: Size = firstFocusedElement.getRect();
      this.whiteboardService.setResizeMode(firstFocusedElement, resizeMode);
      this.resizeMode = firstFocusedElement.getResizeMode();
      const newSize: Size = firstFocusedElement.getRect();

      if (newSize.width !== oldSize.width || newSize.height !== oldSize.height) {
        this.repositionElement(firstFocusedElement, this.resizeModeButton.nativeElement, 'right-bottom');
      }
    }
  }

  private repositionMenu(svgElement: ElementView, positioningElement: HTMLElement, offset?: Point): void {
    const scrollPosition: number = this.whiteboardWrapper.scrollTop;

    const elementRectPoints: Point[] = svgElement.getRotatedRectPoints();

    const x: number = elementRectPoints[0].x;
    const y: number = elementRectPoints[0].y - scrollPosition;

    if (!offset) {
      offset = {x: 0, y: 0};
    }
    positioningElement.style.left = (x + offset.x) + 'px';
    positioningElement.style.top = (y + offset.y) + 'px';
  }
  private repositionElement(svgElement: ElementView, positioningElement: HTMLElement, corner: 'left-top' | 'right-top' | 'right-bottom' | 'left-bottom' = 'left-top'): void {
    const margin = 20;
    const delta = {x: 5, y: 5};

    const scrollPosition: number = this.whiteboardWrapper.scrollTop;
    const container: HTMLElement = this.whiteboard.container.HTML;
    const containerRect: DOMRect = container.getBoundingClientRect();

    const elementRectPoints: Point[] = svgElement.getRotatedRectPoints();

    const visiblePoints: {[key: string]: Point} = {};

    /* check if left-top point is visible */
    if (
      elementRectPoints[0].x > 0 && elementRectPoints[0].y > 0 &&
      elementRectPoints[0].x < containerRect.width && elementRectPoints[0].y < containerRect.height
    ) {
      visiblePoints['left-top'] = {
        x: elementRectPoints[0].x - margin,
        y: elementRectPoints[0].y - margin - delta.y - scrollPosition
      };
    }

    /* check if right-top point is visible */
    if (
      elementRectPoints[1].x > 0 && elementRectPoints[1].y > 0 &&
      elementRectPoints[1].x < containerRect.width && elementRectPoints[1].y < containerRect.height
    ) {
      visiblePoints['right-top'] = {
        x: elementRectPoints[1].x + delta.x,
        y: elementRectPoints[1].y - margin - delta.y - scrollPosition
      };
    }

    /* check if right-bottom point is visible */
    if (
      elementRectPoints[2].x > 0 && elementRectPoints[2].y > 0 &&
      elementRectPoints[2].x < containerRect.width && elementRectPoints[2].y < containerRect.height
    ) {
      visiblePoints['right-bottom'] = {
        x: elementRectPoints[2].x + delta.x,
        y: elementRectPoints[2].y + delta.y - scrollPosition
      };
    }

    /* check if left-bottom point is visible */
    if (
      elementRectPoints[3].x > 0 && elementRectPoints[3].y > 0 &&
      elementRectPoints[3].x < containerRect.width && elementRectPoints[3].y < containerRect.height
    ) {
      visiblePoints['left-bottom'] = {
        x: elementRectPoints[3].x - margin - delta.x,
        y: elementRectPoints[3].y + delta.y - scrollPosition
      };
    }

    let position: Point = visiblePoints[corner];
    if (!position) {
      position = Object.values(visiblePoints)[0];
    }

    positioningElement.style.left = position.x + 'px';
    positioningElement.style.top = position.y + 'px';
  }
  private repositionAllElements(): void {
    if (this.canEdit) {
      const [firstFocusedElement] = this.whiteboard.container.focused.children;
      if (firstFocusedElement) {
        this.repositionElement(firstFocusedElement, this.editButton.nativeElement);
        if (this.canEditCoordinatePlane) {
          this.repositionMenu(firstFocusedElement, this.editCoordinatePlaneMenu.nativeElement);
        } else if (this.canEditNumberLine) {
          this.repositionMenu(firstFocusedElement, this.numberLineEditMenu.nativeElement);
        }
      }
    }

    if (this.showResizeModeButton) {
      const [firstFocusedElement] = this.whiteboard.container.focused.children;
      this.repositionElement(firstFocusedElement, this.resizeModeButton.nativeElement, 'right-bottom');
    }
  }
  private repositionCross(): void {
    const delta = {x: -20, y: -20};

    const activeTool: Tool = this.whiteboard.container.tools.activeTool;
    if (activeTool instanceof DrawTool && (
      activeTool.getDrawer().drawableElement instanceof PointedView ||
      activeTool.getDrawer().drawableElement instanceof RayView)
    ) {
      const drawableElement: Drawable = activeTool.getDrawer().drawableElement;

      const firstPoint: Point = drawableElement.points[0];
      const scrollPosition = this.whiteboardWrapper.scrollTop;
      this.crossButton.nativeElement.style.left = (firstPoint.x + delta.x) + 'px';
      this.crossButton.nativeElement.style.top = (firstPoint.y + delta.y - scrollPosition) + 'px';
    }
  }

  private focusChangedCallBack(): void {
    if (
      this.whiteboard.container.tools.editNodeTool.isOn() ||
      this.whiteboard.container.tools.editTableTool.isOn() ||
      this.whiteboard.container.focused.children.size > 1
    ) {
      return;
    }

    this.canEdit = false;
    this.showEditButton = false;
    this.canEditVertexes = false;
    this.canEditTable = false;
    this.canEditCoordinatePlane = false;
    this.canEditNumberLine = false;

    const [firstFocusedElement] = this.whiteboard.container.focused.children;
    this.canEdit = this.whiteboard.container.focused.children.size !== 0 && (
      (firstFocusedElement instanceof PointedView && firstFocusedElement.editablePointsCount !== 0) ||
      firstFocusedElement instanceof TableView ||
      firstFocusedElement instanceof CoordinatePlaneView2 ||
      firstFocusedElement instanceof NumberLineView2 ||
      firstFocusedElement instanceof CartesianView ||
      firstFocusedElement instanceof GraphicView ||
      firstFocusedElement instanceof RayView ||
      firstFocusedElement instanceof ForeignObjectView && firstFocusedElement.SVG.getAttribute('data-class') === 'ggb-applet'
    );
    if (this.whiteboard.container.focused.children.size !== 0 &&
      firstFocusedElement instanceof ForeignObjectView &&
      !(firstFocusedElement instanceof TextBoxView) &&
      firstFocusedElement.SVG.getAttribute('data-class') !== 'ggb-applet') {

      this.showResizeModeButton = true;
      this.resizeMode = firstFocusedElement.getResizeMode();
    } else {
      this.showResizeModeButton = false;
    }

    this.showEditButton = this.canEdit;

    if (this.canEdit) {
      this.repositionElement(firstFocusedElement, this.editButton.nativeElement);
    } else if (this.showResizeModeButton) {
      this.repositionElement(firstFocusedElement, this.resizeModeButton.nativeElement, 'right-bottom');
    }

    this.whiteboard.container.elements.forEach((element: ElementView) => {
      if (element instanceof ForeignObjectView && element.SVG.getAttribute('data-class') === 'ggb-applet') {
        element.pointerEvents = false;
      }
    });
  }
  private drawClickCallBack(parameters): void {
    /* add cancel button left top of the first point */
    if (this.whiteboard.container.tools.drawTool.getDrawer() instanceof ClickDrawer || this.whiteboard.container.tools.drawTool.getDrawer() instanceof DrawRay) {
      if (parameters.firstCLick) {
        this.clickDrawing = true;

        this.repositionCross();
      }
    }
  }
  private stopClickDrawingCallBack(parameters): void {
    this.clickDrawing = false;
  }

  public onInputChange(event: Event): void {
    const inputElement: HTMLInputElement = (event.target as HTMLInputElement);
    inputElement.value = inputElement.value.replace(/[^0-9.]*/g, '');
  }
}
